import {
  DEFAULT_REPOSITORY_INDEXING_MODE,
  REPOSITORY_INDEXING_MODE_VALUES,
} from "@arti/shared-source-control/indexing/runMode";
import { z } from "zod";

import { normalizeString } from "../stringUtils";
import { repoIndexingContextSchema } from "./repoIndexingContext";
import { sourceControlRepoProfileSchema } from "./schemas/repoProfile";

const normalizedStringSchema = z.preprocess(
  (value) => normalizeString(value),
  z.string().min(1),
);

const nullableNormalizedStringSchema = z.preprocess(
  (value) => normalizeString(value) ?? null,
  z.string().min(1).nullable(),
);

const phaseBudgetOverrideMapSchema = z.record(
  z.string(),
  z.number().int().positive().nullable(),
);

function normalizeUnknownRecord(
  value: unknown,
): Record<string, unknown> | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }

  return Object.fromEntries(Object.entries(value));
}

function normalizeRepoIndexingDispatchEnvelope(value: unknown): unknown {
  const record = normalizeUnknownRecord(value);
  if (!record) {
    return value;
  }

  return {
    default_branch: record.default_branch ?? record.defaultBranch ?? null,
    expected_response_schema_name:
      record.expected_response_schema_name ??
      record.expectedResponseSchemaName ??
      "source_control.repo_profile.v2",
    indexing_mode:
      record.indexing_mode ??
      record.indexingMode ??
      DEFAULT_REPOSITORY_INDEXING_MODE,
    is_required: record.is_required ?? record.isRequired ?? false,
    last_indexed_at: record.last_indexed_at ?? record.lastIndexedAt ?? null,
    phase_budget_overrides: record.phase_budget_overrides,
    provider_id: record.provider_id ?? record.providerId,
    provider_type: record.provider_type ?? record.providerType,
    repo_indexing_context: record.repo_indexing_context,
    repository_id: record.repository_id ?? record.repositoryId ?? null,
    repository_slug: record.repository_slug ?? record.repositorySlug,
    run_id: record.run_id ?? record.runId,
    run_item_id: record.run_item_id ?? record.runItemId,
    source_control_provider_id:
      record.source_control_provider_id ??
      record.provider_id ??
      record.providerId,
    task_id: record.task_id ?? record.taskId,
  };
}

export const repoIndexingDispatchEnvelopeSchema = z.preprocess(
  normalizeRepoIndexingDispatchEnvelope,
  z
    .object({
      default_branch: nullableNormalizedStringSchema.optional().default(null),
      expected_response_schema_name: normalizedStringSchema.default(
        "source_control.repo_profile.v2",
      ),
      indexing_mode: z
        .enum(REPOSITORY_INDEXING_MODE_VALUES)
        .default(DEFAULT_REPOSITORY_INDEXING_MODE),
      is_required: z.boolean().optional().default(false),
      last_indexed_at: z
        .string()
        .datetime()
        .nullable()
        .optional()
        .default(null),
      phase_budget_overrides: phaseBudgetOverrideMapSchema.optional(),
      provider_id: normalizedStringSchema,
      provider_type: normalizedStringSchema,
      repo_indexing_context: repoIndexingContextSchema,
      repository_id: nullableNormalizedStringSchema.optional().default(null),
      repository_slug: normalizedStringSchema,
      run_id: normalizedStringSchema,
      run_item_id: normalizedStringSchema,
      source_control_provider_id: normalizedStringSchema,
      task_id: normalizedStringSchema,
    })
    .strict(),
);

export type RepoIndexingDispatchEnvelope = z.infer<
  typeof repoIndexingDispatchEnvelopeSchema
>;

export const repoIndexingCompletionEnvelopeSchema = z
  .object({
    chunks_indexed_at: z.string().datetime().nullable().optional(),
    chunks_indexed_commit_sha: z.string().nullable().optional(),
    embedding_completed: z.boolean().optional(),
    expected_response_schema_name: normalizedStringSchema,
    indexing_mode: z.enum(REPOSITORY_INDEXING_MODE_VALUES).optional(),
    parsed_result: sourceControlRepoProfileSchema.nullable(),
    profile_indexed_at: z.string().datetime().nullable().optional(),
    profile_indexed_commit_sha: z.string().nullable().optional(),
    repo_profile: sourceControlRepoProfileSchema.nullable().optional(),
    stderr: z.string(),
    stdout: z.string(),
  })
  .strict();

export type RepoIndexingCompletionEnvelope = z.infer<
  typeof repoIndexingCompletionEnvelopeSchema
>;

export const repoIndexingTaskMetadataSchema = z
  .object({
    flow: z
      .object({ kind: normalizedStringSchema.optional() })
      .loose()
      .optional(),
    repository_id: nullableNormalizedStringSchema.optional(),
    repository_slug: normalizedStringSchema.optional(),
    run_id: normalizedStringSchema.optional(),
    run_item_id: normalizedStringSchema.optional(),
    source_control_provider_id: normalizedStringSchema.optional(),
  })
  .loose();

export type RepoIndexingTaskMetadata = z.infer<
  typeof repoIndexingTaskMetadataSchema
>;

export const repoIndexingTaskCallbackPayloadSchema = z
  .object({
    error: z.string().nullable().optional(),
    metadata: z.preprocess(
      normalizeUnknownRecord,
      repoIndexingTaskMetadataSchema.optional(),
    ),
    parsed_result: z.unknown().optional(),
    repository_id: nullableNormalizedStringSchema.optional(),
    result: z.preprocess(
      normalizeUnknownRecord,
      z.object({}).catchall(z.unknown()).optional(),
    ),
    run_id: normalizedStringSchema.optional(),
    run_item_id: normalizedStringSchema.optional(),
    status: normalizedStringSchema.optional(),
  })
  .loose();

export type RepoIndexingTaskCallbackPayload = z.infer<
  typeof repoIndexingTaskCallbackPayloadSchema
>;

export const repositoryChatRetrievalModeSchema = z.enum([
  "hybrid",
  "indexed_first",
  "indexed_only",
  "live_only",
]);

export const repoChatIndexedSnippetSchema = z
  .object({
    codeSnippet: z.string(),
    endLine: z.number().int().positive(),
    path: z.string(),
    repositorySlug: z.string(),
    startLine: z.number().int().positive(),
  })
  .strict();

export const repoChatRetrieveDispatchEnvelopeSchema = z
  .object({
    branchScopeByRepo: z.record(z.string(), z.string()).nullable(),
    conversationId: normalizedStringSchema,
    effectiveRepoScope: z.array(z.string()),
    evidenceLimits: z
      .object({
        maxItemsPerRepo: z.number().int().positive(),
        maxItemsTotal: z.number().int().positive(),
        maxRepos: z.number().int().positive(),
      })
      .strict(),
    indexedSnippets: z.array(repoChatIndexedSnippetSchema),
    question: normalizedStringSchema,
    rankedRepos: z.array(
      z.object({ score: z.number(), slug: normalizedStringSchema }).strict(),
    ),
    requestedRepoScope: z.array(z.string()),
    retrievalMode: repositoryChatRetrievalModeSchema,
    turnId: normalizedStringSchema,
  })
  .strict();

export type RepoChatRetrieveDispatchEnvelope = z.infer<
  typeof repoChatRetrieveDispatchEnvelopeSchema
>;

export const repoChatRetrieveEvidenceSchema = z
  .object({
    branch: z.string().nullable().optional(),
    endLine: z.number().int().positive(),
    freshness: z.string(),
    path: z.string(),
    relevance: z.number(),
    repositorySlug: z.string(),
    sourceType: z.string(),
    startLine: z.number().int().positive(),
    summary: z.string(),
  })
  .strict();

export const repoChatRetrieveResultSchema = z
  .object({
    consultedRepos: z.array(z.string()),
    effectiveRepoScope: z.array(z.string()),
    evidence: z.array(repoChatRetrieveEvidenceSchema),
    freshnessByRepo: z.record(z.string(), z.string()),
    recommendedFollowups: z.array(z.string()),
    unresolvedGaps: z.array(z.string()),
  })
  .strict();

export type RepoChatRetrieveResult = z.infer<
  typeof repoChatRetrieveResultSchema
>;

export const repoChatRetrieveCompletionEnvelopeSchema = z
  .object({
    parsed_result: repoChatRetrieveResultSchema,
    stderr: z.string(),
    stdout: z.string(),
  })
  .strict();

export type RepoChatRetrieveCompletionEnvelope = z.infer<
  typeof repoChatRetrieveCompletionEnvelopeSchema
>;

export const repoChatTaskMetadataSchema = z
  .object({
    conversation_id: normalizedStringSchema.optional(),
    flow: z
      .object({ kind: normalizedStringSchema.optional() })
      .loose()
      .optional(),
    source_control_provider_id: normalizedStringSchema.optional(),
    source_control_repository_allowlist: z
      .array(normalizedStringSchema)
      .optional(),
    source_control_selected_branches: z
      .record(z.string(), normalizedStringSchema)
      .optional(),
    turn_id: normalizedStringSchema.optional(),
  })
  .loose();

export type RepoChatTaskMetadata = z.infer<typeof repoChatTaskMetadataSchema>;

export const repoChatTaskCallbackPayloadSchema = z
  .object({
    metadata: z.preprocess(
      normalizeUnknownRecord,
      repoChatTaskMetadataSchema.optional(),
    ),
    parsed_result: z.unknown().optional(),
    result: z.preprocess(
      normalizeUnknownRecord,
      z.object({}).catchall(z.unknown()).optional(),
    ),
    status: normalizedStringSchema.optional(),
    turn_id: normalizedStringSchema.optional(),
  })
  .loose();

export type RepoChatTaskCallbackPayload = z.infer<
  typeof repoChatTaskCallbackPayloadSchema
>;

export function parseRepoIndexingDispatchEnvelope(
  value: unknown,
): RepoIndexingDispatchEnvelope | null {
  const parsed = repoIndexingDispatchEnvelopeSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function serializeRepoIndexingDispatchEnvelope(
  envelope: RepoIndexingDispatchEnvelope,
): RepoIndexingDispatchEnvelope {
  return repoIndexingDispatchEnvelopeSchema.parse(envelope);
}

export function parseRepoIndexingCompletionEnvelope(
  value: unknown,
): RepoIndexingCompletionEnvelope | null {
  const parsed = repoIndexingCompletionEnvelopeSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function serializeRepoIndexingCompletionEnvelope(
  envelope: RepoIndexingCompletionEnvelope,
): RepoIndexingCompletionEnvelope {
  return repoIndexingCompletionEnvelopeSchema.parse(envelope);
}

export function parseRepoIndexingTaskCallbackPayload(
  value: unknown,
): RepoIndexingTaskCallbackPayload | null {
  const parsed = repoIndexingTaskCallbackPayloadSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function extractRepoIndexingResultEnvelope(
  value: unknown,
): RepoIndexingCompletionEnvelope | null {
  const direct = parseRepoIndexingCompletionEnvelope(value);
  if (direct) {
    return direct;
  }

  const callbackPayload = parseRepoIndexingTaskCallbackPayload(value);
  if (!callbackPayload) {
    return null;
  }

  return (
    parseRepoIndexingCompletionEnvelope(callbackPayload.result) ??
    parseRepoIndexingCompletionEnvelope(callbackPayload.parsed_result)
  );
}

export function parseRepoChatRetrieveDispatchEnvelope(
  value: unknown,
): RepoChatRetrieveDispatchEnvelope | null {
  const parsed = repoChatRetrieveDispatchEnvelopeSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function serializeRepoChatRetrieveDispatchEnvelope(
  envelope: RepoChatRetrieveDispatchEnvelope,
): RepoChatRetrieveDispatchEnvelope {
  return repoChatRetrieveDispatchEnvelopeSchema.parse(envelope);
}

export function parseRepoChatRetrieveCompletionEnvelope(
  value: unknown,
): RepoChatRetrieveCompletionEnvelope | null {
  const parsed = repoChatRetrieveCompletionEnvelopeSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function serializeRepoChatRetrieveCompletionEnvelope(
  envelope: RepoChatRetrieveCompletionEnvelope,
): RepoChatRetrieveCompletionEnvelope {
  return repoChatRetrieveCompletionEnvelopeSchema.parse(envelope);
}

export function parseRepoChatTaskCallbackPayload(
  value: unknown,
): RepoChatTaskCallbackPayload | null {
  const parsed = repoChatTaskCallbackPayloadSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function extractRepoChatRetrieveResult(
  value: unknown,
): RepoChatRetrieveResult | null {
  const directResult = repoChatRetrieveResultSchema.safeParse(value);
  if (directResult.success) {
    return directResult.data;
  }

  const completionEnvelope = parseRepoChatRetrieveCompletionEnvelope(value);
  if (completionEnvelope) {
    return completionEnvelope.parsed_result;
  }

  const callbackPayload = parseRepoChatTaskCallbackPayload(value);
  if (!callbackPayload) {
    return null;
  }

  const nestedEnvelope = parseRepoChatRetrieveCompletionEnvelope(
    callbackPayload.result,
  );
  if (nestedEnvelope) {
    return nestedEnvelope.parsed_result;
  }

  const parsedResult = repoChatRetrieveResultSchema.safeParse(
    callbackPayload.parsed_result,
  );
  return parsedResult.success ? parsedResult.data : null;
}
